import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BrainLoadingIcon } from 'morpheus-ui';
import { Typography } from '../../../core/components/molecules/Typography';
import { Alert } from '../../../core/components/atoms/Alert';
import { Button } from '../../../core/components/atoms/Button';
import { SocialProvider } from '../config/socialProviders';
import {
  SOCIAL_AUTH_ERROR_MESSAGE,
  SOCIAL_AUTH_ERROR_TITLE,
} from '../constants/socialAuth';
import { socialCallback } from '../queries/fetchAuth';

interface SocialAuthCallbackProps {
  provider: SocialProvider;
}

/**
 * Página de retorno do provedor social: troca `code` + `state` pela sessão e
 * segue para a home. Vale para Google e Apple — o fluxo é o mesmo porque a
 * ponte da Apple entrega o código na query string, igual ao Google.
 */
export default function SocialAuthCallback({ provider }: SocialAuthCallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    // Clears the OAuth params from the URL without retriggering this effect,
    // so the authorization code/state don't linger in the address bar or history.
    const failWithError = () => {
      setErrorMessage(SOCIAL_AUTH_ERROR_MESSAGE);
      window.history.replaceState(null, '', location.pathname);
    };

    if (oauthError) {
      failWithError();
      return;
    }

    if (!code || !state) {
      failWithError();
      return;
    }

    socialCallback(provider.id, { code, state, application: 'web' })
      .then(() => {
        navigate(`/`, { replace: true });
      })
      .catch(() => {
        failWithError();
      });
  }, [location.search, location.pathname, navigate, provider.id]);

  if (errorMessage) {
    return (
      <div className="flex h-screen flex-col items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-level-4">
          <Alert
            variant="error"
            title={SOCIAL_AUTH_ERROR_TITLE}
            message={errorMessage}
            className="mb-6"
          />
          <Button variant="contained" size="large" full asLink to="/login">
            Voltar para o login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center p-4 text-center">
      <BrainLoadingIcon className="mb-6 h-20 w-20 animate-pulse stroke-2 stroke-brand-color-primary-pure" />
      <Typography size="h2" type="title" weight="bold" className="mb-2">
        {provider.callbackTitle}
      </Typography>
      <Typography size="default">
        Aguarde, estamos finalizando seu acesso à plataforma.
      </Typography>
    </div>
  );
}
